import { ArrowRight, LoaderCircle } from "lucide-react";
import type { ReactNode } from "react";
import styles from "./choice-list.module.css";

export interface Choice {
  id: string;
  /** Two or three letters in the tile, e.g. a country code. */
  mark: string;
  title: string;
  subtitle?: ReactNode;
}

export interface ChoiceListProps {
  choices: Choice[];
  onChoose: (id: string) => void;
  /** The choice being applied: it shows a spinner and the others are disabled. */
  pendingId?: string | null;
  /** Highlighted as the current one, e.g. the company the session is in. */
  currentId?: string | null;
  label: string;
}

/**
 * A short list of things to pick one of, each a full-width button: companies in the console
 * (Choose a company, `VHsi2`), profiles in the portals.
 */
export function ChoiceList({ choices, onChoose, pendingId = null, currentId = null, label }: ChoiceListProps) {
  return (
    <ul className={styles.list} aria-label={label}>
      {choices.map((choice) => {
        const pending = pendingId === choice.id;
        return (
          <li key={choice.id}>
            <button
              type="button"
              className={styles.choice}
              data-current={choice.id === currentId ? "" : undefined}
              aria-busy={pending || undefined}
              disabled={pendingId !== null}
              onClick={() => onChoose(choice.id)}
            >
              <span className={styles.mark} aria-hidden="true">
                {choice.mark}
              </span>
              <span className={styles.text}>
                <span className={styles.title}>{choice.title}</span>
                {choice.subtitle ? <span className={styles.subtitle}>{choice.subtitle}</span> : null}
              </span>
              {pending ? (
                <LoaderCircle className={styles.spin} size={18} aria-hidden="true" />
              ) : (
                <ArrowRight size={18} aria-hidden="true" />
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
